import {
  LOAD_SKILL_TOOL_NAME,
  READ_SKILL_RESOURCE_TOOL_NAME,
  RUN_SKILL_SCRIPT_TOOL_NAME,
  SEARCH_SKILLS_TOOL_NAME,
} from "../constants/skills";
import { TERMINAL_SKILL_SCRIPT_RUN_STATES } from "../types/skill-sandbox";
import type { IFormatSkillRunStepOutputProps } from "./skill-run-step.types";

// NOTE: NSAI-6698 — the readable form of a skill tool step in an agent run's
// trace, shared so web and mobile print the same lines. The server stores a
// summary object for these steps (server: chat/tools/skills/
// skill-tool-trace.util.ts); `kind` values mirror its ESkillToolTraceKind.
//
// Rows recorded before that summary exist hold the model-facing text cut to
// 200 characters — a wrapper tag and half a trust notice, nothing a person can
// use — so they are replaced by one line saying the detail was not kept.

const SKILL_TOOL_NAMES: ReadonlySet<string> = new Set([
  LOAD_SKILL_TOOL_NAME,
  READ_SKILL_RESOURCE_TOOL_NAME,
  RUN_SKILL_SCRIPT_TOOL_NAME,
  SEARCH_SKILLS_TOOL_NAME,
]);

const LEGACY_SKILL_OUTPUT_PREFIX = "<skill_";

// NOTE: The terminal run states, taken from the shared enum so a renamed state
// cannot silently fall back to its raw server string here.
const KNOWN_RUN_RESULTS: ReadonlySet<string> = new Set<string>(
  TERMINAL_SKILL_SCRIPT_RUN_STATES,
);

const readString = (props: {
  record: Record<string, unknown>;
  key: string;
}): string => {
  const value = props.record[props.key];
  return typeof value === "string" ? value : "";
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const formatScriptRun = (props: {
  record: Record<string, unknown>;
  translate: IFormatSkillRunStepOutputProps["translate"];
}): string => {
  const { record, translate } = props;
  const exitCode = record["exitCode"];
  const inputFiles = readString({ record, key: "inputFiles" });
  const outputFiles = readString({ record, key: "outputFiles" });
  const stdout = readString({ record, key: "stdout" });

  const result = readString({ record, key: "result" });
  const lines = [
    translate("runs.trace.skill.ranScript", {
      script: readString({ record, key: "script" }),
      // NOTE: A state the client does not know yet is shown as the server sent
      // it rather than as a missing translation key.
      result: KNOWN_RUN_RESULTS.has(result)
        ? translate(`runs.trace.skill.result.${result}`)
        : result,
    }),
  ];
  if (typeof exitCode === "number") {
    lines.push(translate("runs.trace.skill.exitCode", { code: exitCode }));
  }
  lines.push(
    translate("runs.trace.skill.inputFiles", {
      files:
        inputFiles.length > 0
          ? inputFiles
          : translate("runs.trace.skill.noFiles"),
    }),
  );
  if (outputFiles.length > 0) {
    lines.push(
      translate("runs.trace.skill.outputFiles", { files: outputFiles }),
    );
  }
  if (stdout.trim().length > 0) {
    lines.push("", translate("runs.trace.skill.stdout"), stdout);
  }
  return lines.join("\n");
};

// NOTE: `null` means "not a skill step" — the caller renders the payload as it
// always has. A recognised step always gets text, never the raw object.
export const formatSkillRunStepOutput = ({
  output,
  toolName,
  translate,
}: IFormatSkillRunStepOutputProps): string | null => {
  if (toolName === null || !SKILL_TOOL_NAMES.has(toolName)) {
    return null;
  }

  if (
    typeof output === "string" &&
    output.startsWith(LEGACY_SKILL_OUTPUT_PREFIX)
  ) {
    return translate("runs.trace.skill.outputHidden");
  }
  if (!isRecord(output)) {
    return null;
  }

  switch (output["kind"]) {
    case "skill_loaded":
      return translate("runs.trace.skill.loaded", {
        name: readString({ record: output, key: "skill" }),
        count:
          typeof output["resourceCount"] === "number"
            ? output["resourceCount"]
            : 0,
      });
    case "skill_file_read":
      return translate("runs.trace.skill.readFile", {
        path: readString({ record: output, key: "path" }),
      });
    case "skill_script_run":
      return formatScriptRun({ record: output, translate });
    // NOTE: NSAI-6772 — names only; the server keeps no description in the trace.
    // A miss still names its keywords, so a person can see which query found
    // nothing; only a step recorded without a query falls back to the bare line.
    case "skill_search": {
      const skills = readString({ record: output, key: "skills" });
      const query = readString({ record: output, key: "query" });
      if (skills.length > 0) {
        return translate("runs.trace.skill.searched", { query, skills });
      }
      return query.length === 0
        ? translate("runs.trace.skill.searchNoMatch")
        : translate("runs.trace.skill.searchNoMatchFor", { query });
    }
    case "skill_tool_refused":
      return readString({ record: output, key: "message" });
    default:
      return null;
  }
};
